import { ref, watch, computed } from '@vue/composition-api'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useToast } from 'vue-toastification/composition'
import store from '@/store'
import router from '@/router/index'

// Notification

export default function useConfirmedList() {
  // Use toast
  const toast = useToast()
  const route = router.history.current

  const refConfirmedBookingsTable = ref(null)

  // Table Handlers
  const tableColumns = [
    {
      key: 'stringID',
      label: 'ID',
      sortable: true,
    },
    {
      key: 'start',
      label: 'appt. time',
      sortable: true,
    },
    {
      key: 'activityType',
      label: 'Activity type',
      sortable: true,
    },
    {
      key: 'paymentRemarks',
      label: 'Payment Remarks',
      sortable: true,
    },
    {
      key: 'customerName',
      label: 'Customer',
      sortable: true,
    },
    {
      key: 'agentName',
      label: 'Agent',
      sortable: true,
    },
    {
      key: 'createdBy',
      label: 'Created by',
      sortable: true,
    },
    { key: 'status', sortable: true },
    { key: 'actions' },
  ]
  const perPage = ref(route.query.perPage ?? process.env.VUE_APP_PER_PAGE_CONTENT)
  const totalBookings = ref(0)
  const currentPage = ref(route.query.page ?? 1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref(route.query.search ?? '')
  const sortBy = ref(route.query.sortBy ?? 'stringID')
  const isSortDirDesc = ref(route.query.sortDesc === 'true')
  const statusFilter = ref([])
  if (Array.isArray(route.query.status)) {
    statusFilter.value = route.query.status
  } else if (route.query.status) {
    statusFilter.value = [route.query.status]
  }
  const toDeletedID = ref(null)
  const type = ref('confirmed')
  const bookingData = ref([])
  const activityTypeFilter = ref([])
  if (Array.isArray(route.query.activityType)) {
    activityTypeFilter.value = route.query.activityType
  } else if (route.query.activityType) {
    activityTypeFilter.value = [route.query.activityType]
  }
  const startDateFilter = ref(route.query.startDate || '')
  let initialLoad = true

  const dataMeta = computed(() => {
    const localItemsCount = refConfirmedBookingsTable.value ? refConfirmedBookingsTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalBookings.value,
    }
  })

  const refetchData = () => {
    refConfirmedBookingsTable.value.refresh()
  }

  watch([searchQuery, statusFilter, activityTypeFilter, startDateFilter], () => {
    currentPage.value = 1
    refetchData()
  }, { deep: true })

  watch([currentPage], () => {
    refetchData()
  }, { deep: true })

  const fetchBookings = (ctx, callback) => {
    store
      .dispatch('app-confirmed-bookings/fetchBookings', {
        search: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        status: statusFilter.value,
        activityType: activityTypeFilter.value,
        startDate: startDateFilter.value,
        type: type.value,
      },
      { root: true })
      .then(response => {
        const { bookings, total } = response.data
        bookingData.value = bookings
        callback(bookings)
        totalBookings.value = total
        if (!initialLoad) {
          router.replace({
            query: {
              search: searchQuery.value,
              perPage: perPage.value,
              page: currentPage.value,
              sortBy: sortBy.value,
              sortDesc: isSortDirDesc.value,
              status: statusFilter.value,
              activityType: activityTypeFilter.value,
              startDate: startDateFilter.value,
            },
          }).catch(() => {})
        }
        initialLoad = false
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const deleteBooking = () => {
    store
      .dispatch('app-confirmed-bookings/deleteBooking', {
        id: toDeletedID.value,
      },
      { root: true })
      .then(response => {
        refetchData()
        toast({
          component: ToastificationContent,
          props: {
            title: response.data.message ?? '',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .catch(error => {
        toast({
          component: ToastificationContent,
          props: {
            title: error.data.message || process.env.VUE_APP_DEFAULT_ERROR_MESSAGE,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*

  const resolveBookingStatusVariant = status => {
    if (status === 'confirmed') return 'success'
    if (status === 'pending-payment') return 'warning'
    if (status === 'pending-approval') return 'warning'
    if (status === 'waiting-list') return 'secondary'
    if (status === 'draft') return 'secondary'
    if (status === 'cancelled') return 'danger'
    return 'primary'
  }

  const resolvePaymentStatusVariant = status => {
    if (status === 'Fully Paid') return 'success'
    if (status === 'Partially Paid') return 'warning'
    if (status === 'Unpaid') return 'danger'
    if (status === 'Overpaid') return 'info'
    if (status === 'Refunded') return 'secondary'
    return 'secondary'
  }

  return {
    fetchBookings,
    tableColumns,
    perPage,
    currentPage,
    totalBookings,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refConfirmedBookingsTable,

    resolveBookingStatusVariant,
    resolvePaymentStatusVariant,
    refetchData,

    toDeletedID,
    deleteBooking,
    type,
    bookingData,

    // Extra Filters
    statusFilter,
    activityTypeFilter,
    startDateFilter,
  }
}
